import React from 'react';
import {
  UserGroupIcon,
  MapPinIcon,
  ClockIcon,
  TruckIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

export default function OnlineHelp() {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <UserGroupIcon className="mx-auto h-16 w-16 text-blue-600 mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Staff Online Tracking</h1>
        <p className="text-lg text-gray-600">Real-time monitoring of staff clock-in status, locations, and activities</p>
      </div>

      {/* Overview Section */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <CheckBadgeIcon className="h-6 w-6 text-blue-600 mr-2" />
          Overview
        </h2>
        <p className="text-gray-700 mb-4">
          The Staff Online Tracking system provides real-time visibility into your team's clock-in status, work locations,
          break times, and meeting activities. This comprehensive dashboard helps managers monitor workforce attendance,
          track staff movements, and ensure operational efficiency across all locations.
        </p>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <p className="text-sm text-blue-700">
            <strong>Key Features:</strong> Live staff tracking, location mapping, break monitoring, meeting status updates, and real-time WebSocket connectivity.
          </p>
        </div>
      </div>

      {/* Interface Layout */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Interface Overview</h2>

        {/* Header Section */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Dashboard Header</h3>
          <div className="bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-semibold text-gray-900">Staff Online: 5</h4>
                <p className="text-sm text-gray-600">Real-time count of currently clocked-in staff members</p>
              </div>
              <div className="text-sm text-gray-500">
                Auto-updates every minute via WebSocket
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Desktop Table View</h3>
          <div className="bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Staff Name</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Role</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Clock-In Time</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Break Time</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Meeting Status</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="bg-yellow-50">
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      <div className="font-medium text-blue-900 opacity-75 cursor-not-allowed">John Smith</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Sales Manager</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">09:15</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="text-gray-700">12:30 - 13:00</div>
                      <div className="text-xs text-gray-500">30 mins</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        🟢 Working
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        🚗 Traveling
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <button className="text-blue-500 hover:text-blue-700 opacity-75 cursor-not-allowed">
                        <MapPinIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                  <tr className="bg-red-50">
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      <div className="font-medium text-blue-900 opacity-75 cursor-not-allowed">Sarah Johnson</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Developer</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">08:45</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="text-gray-500">N/A</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        🔴 On Break
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        💼 In Office
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <button className="text-blue-500 hover:text-blue-700 opacity-75 cursor-not-allowed">
                        <MapPinIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Mobile Card View</h3>
          <div className="bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300">
            <div className="space-y-4">
              <div className="rounded-lg shadow overflow-hidden border-l-4 border-yellow-400">
                <div className="p-4 bg-yellow-50">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="text-base font-medium text-blue-900 opacity-75 cursor-not-allowed">John Smith</div>
                      <p className="text-sm text-gray-600">Sales Manager</p>
                    </div>
                    <button className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 opacity-75 cursor-not-allowed">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      View Location
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      🟢 Working
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      🚗 Traveling
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-2">
                    <div className="bg-white rounded p-2">
                      <p className="text-xs text-gray-500">Clock-In Time</p>
                      <p className="font-medium">09:15</p>
                    </div>
                    <div className="bg-white rounded p-2">
                      <p className="text-xs text-gray-500">Break</p>
                      <div>
                        <p className="font-medium">12:30 - 13:00</p>
                        <p className="text-xs text-gray-500">30 mins</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
            Real-Time Tracking
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Live WebSocket connectivity for instant updates
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Automatic refresh every minute as backup
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Real-time staff count in header
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Instant status changes (working/break/meeting)
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <MapPinIcon className="h-5 w-5 text-green-600 mr-2" />
            Location Services
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">•</span>
              GPS coordinate tracking at clock-in
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">•</span>
              Interactive map view for location details
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">•</span>
              Location-based attendance verification
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">•</span>
              Remote work location monitoring
            </li>
          </ul>
        </div>
      </div>

      {/* Meeting Sequence Status */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Meeting Sequence Status</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🚗</span>
              <h3 className="font-semibold text-yellow-800">Traveling</h3>
            </div>
            <p className="text-sm text-yellow-700">Staff member is traveling to a meeting location</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">📍</span>
              <h3 className="font-semibold text-blue-800">At Meeting</h3>
            </div>
            <p className="text-sm text-blue-700">Currently at the meeting location</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🏢</span>
              <h3 className="font-semibold text-green-800">Back in Office</h3>
            </div>
            <p className="text-sm text-green-700">Returned to office after meeting</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🤝</span>
              <h3 className="font-semibold text-purple-800">In Meeting</h3>
            </div>
            <p className="text-sm text-purple-700">Actively participating in a meeting</p>
          </div>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-white rounded-lg p-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Usage Guidelines</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">When to Use</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Monitor daily staff attendance and punctuality
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Track field staff locations and movements
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Manage break times and ensure compliance
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Coordinate meeting schedules and attendance
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Best Practices</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Check the dashboard at start of business day
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Use location tracking for remote workforce
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Monitor break durations for fairness
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Address attendance issues promptly
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
            <div>
              <p className="text-sm text-yellow-700">
                <strong>Privacy Note:</strong> Location tracking requires explicit consent and should comply with local privacy regulations.
                GPS coordinates are stored securely and only accessible to authorized management personnel.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}